import { createRouter, type ToOptions } from '@tanstack/react-router';

import { NotFoundPage } from '$/pages/NotFoundPage';
import { NewPasswordRoute } from '$/routes/Account/NewPasswordRoute';
import { PasswordResetSuccessfulRoute } from '$/routes/Account/PasswordResetSuccessfulRoute';
import { RegistrationSuccessfulPage } from '$/routes/Account/RegistrationSuccessfulPage';
import { AdminAiPromptsRoute } from '$/routes/Admin/AdminAiPromptsRoute';
import { AdminBaseRouteLayout } from '$/routes/Admin/AdminBaseRouteLayout';
import { AdminCollectionsRoute } from '$/routes/Admin/AdminCollectionsRoute';
import { AdminContactRoute } from '$/routes/Admin/AdminContactRoute';
import { AdminCountriesRoute } from '$/routes/Admin/AdminCountriesRoute';
import { AdminInspirationsRoute } from '$/routes/Admin/AdminInspirationsRoute';
import { AdminKeyUserManagementRoute } from '$/routes/Admin/AdminKeyUserManagementRoute';
import { AdminLanguagesRoute } from '$/routes/Admin/AdminLanguagesRoute';
import { AdminProducersRoute } from '$/routes/Admin/AdminProducersRoute';
import { AdminSchoolsRoute } from '$/routes/Admin/AdminSchoolRequestsRoute';
import { AdminUserManagementRoute } from '$/routes/Admin/AdminUserManagementRoute';
import { CollectionsRoute } from '$/routes/Collections/CollectionsRoute';
import { ColorCollectionsRoute } from '$/routes/Collections/ColorCollectionsRoute';
import { FacadeCollectionsRoute } from '$/routes/Collections/FacadeCollectionsRoute';
import { FloorsCollectionRoute } from '$/routes/Collections/FloorsCollectionRoute';
import { WallsCollectionRoute } from '$/routes/Collections/WallsCollectionRoute';
import { DashboardRoute } from '$/routes/DashboardRoute';
import { DesignGuideHarmonyRoute } from '$/routes/DesignGuides/DesignGuideHarmonyRoute';
import { DesignGuideRoute } from '$/routes/DesignGuides/DesignGuideRoute';
import { EditorRoute } from '$/routes/Editor/EditorRoute';
import { FavoritesRoute } from '$/routes/FavoritesRoute';
import { CookieDeclarationRoute } from '$/routes/General/CookieDeclarationRoute';
import { LinkExpiredRoute } from '$/routes/General/LinkExpiredRoute';
import { NotAllowedRoute } from '$/routes/General/NotAllowedRoute';
import { NotFoundRoute } from '$/routes/General/NotFoundRoute';
import { InspirationEditorRoute } from '$/routes/Inspirations/InspirationEditorRoute';
import { InspirationProjectRoute } from '$/routes/Inspirations/InspirationProjectRoute';
import { InspirationsRoute } from '$/routes/Inspirations/InspirationsRoute';
import { LegacyInspirationEditorRoute } from '$/routes/Inspirations/LegacyInspirationEditorRoute';
import { InvitationRoute } from '$/routes/InvitationRoute';
import { BaseRouteLayout } from '$/routes/Layouts/BaseRouteLayout';
import { EditorRouteLayout } from '$/routes/Layouts/EditorLayout';
import { ImprintRoute } from '$/routes/Legal/ImprintRoute';
import { IntegrityRoute } from '$/routes/Legal/IntegrityRoute';
import { LicenseRoute } from '$/routes/Legal/LicenseRoute';
import { PrivacyRoute } from '$/routes/Legal/PrivacyRoute';
import { TermsOfServiceRoute } from '$/routes/Legal/TermsOfServiceRoute';
import { OidcLoginRoute, OidcCallbackRoute } from '$/routes/OidcRoutes';
import { PricingRoute } from '$/routes/PricingRoute';
import { ProjectsRoute } from '$/routes/ProjectsRoute';
import { RootRoute } from '$/routes/RootRoute';
import { SchoolClassesRoute } from '$/routes/School/SchoolClassesRoute';
import { SchoolClassRoute } from '$/routes/School/SchoolClassRoute';
import { SchoolRoute } from '$/routes/School/SchoolRoute';
import { StudentsRoute } from '$/routes/School/StudentsRoute';
import { queryClient } from '$/services/client';

export const routeTree = RootRoute.addChildren([
  InspirationEditorRoute,
  InspirationProjectRoute,
  LegacyInspirationEditorRoute,
  CookieDeclarationRoute,
  NotAllowedRoute,
  NotFoundRoute,
  LinkExpiredRoute,
  NewPasswordRoute,
  RegistrationSuccessfulPage,
  PricingRoute,
  InvitationRoute,
  BaseRouteLayout.addChildren([
    DashboardRoute,
    DesignGuideRoute,
    DesignGuideHarmonyRoute,
    InspirationsRoute,
    ProjectsRoute,
    CollectionsRoute,
    FavoritesRoute,
    ColorCollectionsRoute,
    FloorsCollectionRoute,
    WallsCollectionRoute,
    FacadeCollectionsRoute,
    PasswordResetSuccessfulRoute,
    SchoolRoute,
    SchoolClassesRoute,
    SchoolClassRoute,
    StudentsRoute,
  ]),
  AdminBaseRouteLayout.addChildren([
    AdminInspirationsRoute,
    AdminUserManagementRoute,
    AdminKeyUserManagementRoute,
    AdminCollectionsRoute,
    AdminAiPromptsRoute,
    AdminLanguagesRoute,
    AdminCountriesRoute,
    AdminContactRoute,
    AdminProducersRoute,
    AdminSchoolsRoute,
  ]),
  EditorRouteLayout.addChildren([
    // PhotoserviceRoute,
    // PhotoserviceProjectRoute,
    EditorRoute,
  ]),
  ImprintRoute,
  IntegrityRoute,
  LicenseRoute,
  PrivacyRoute,
  TermsOfServiceRoute,
  OidcLoginRoute,
  OidcCallbackRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: false,
  context: { client: queryClient },
  notFoundMode: 'root',
  defaultNotFoundComponent: () => <NotFoundPage />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export const getCurrentRouterParams = () => {
  return router.__store.state.matches.at(-1)?.params as
    | Record<string, string>
    | undefined;
};

export type TypedToOptions = ToOptions<typeof router>['to'];
